import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useScrollReveal } from "./useScrollReveal";
import LandingNav from "./LandingNav";
import Hero from "./Hero";
import Features from "./Features";
import Workflow from "./Workflow";
import Spotlights from "./Spotlights";
import AIAssistants from "./AIAssistants";
import LabExperience from "./LabExperience";
import Dashboards from "./Dashboards";
import Solutions from "./Solutions";
import StatsBand from "./StatsBand";
import LandingFooter from "./LandingFooter";
import "./landing.css";
import Certification from "./Certification";
import TksaSection from "./TksaSection";
import OtSpotlight from "./OtSpotlight";
import LoginTerminal from "./auth/LoginTerminal";
import SignupTerminal from "./auth/SignupTerminal";

export default function LandingPage() {
  const navigate = useNavigate();
  const { user, isInitialized } = useAuth();
  const pageRef = useRef(null);
  /* null | "login" | "signup" — auth happens over this page, never a route away. */
  const [authMode, setAuthMode] = useState(null);
  const closeAuth = () => setAuthMode(null);

  useScrollReveal();

  // Reveal the hero cascade only after web fonts load, so the serif headline
  // never paints in a fallback face and then reflows (the first-paint "blink").
  useEffect(() => {
    const page = pageRef.current;
    if (!page) return;

    let settled = false;
    const reveal = () => {
      if (settled) return;
      settled = true;
      page.classList.add("fonts-ready");
    };

    if (document.fonts?.ready) {
      document.fonts.ready.then(reveal);
    } else {
      reveal();
    }
    // Safety net: never keep content hidden if font loading stalls.
    const timeout = setTimeout(reveal, 1500);
    return () => clearTimeout(timeout);
  }, []);

  // Send signed-in users to their dashboard — but not while a console is open,
  // or it would be torn down mid-trace the instant the token lands.
  useEffect(() => {
    if (authMode) return;
    if (isInitialized && user) navigate("/home", { replace: true });
  }, [isInitialized, user, navigate, authMode]);

  return (
    <div
      ref={pageRef}
      dir="ltr"
      className="lp-page relative min-h-screen overflow-x-hidden"
    >
      {/* Decorative ambient layer — above the global canvas, behind content.
          Static (no mouse tracking) so it never repaints on cursor move, which
          keeps the background particle canvas smooth. */}
      <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none">
        <div className="glow-blob glow-blob-purple absolute top-[-12%] right-[-8%]" />
        <div className="glow-blob glow-blob-green absolute top-[55%] left-[-12%]" />
      </div>

      <LandingNav
        onLogin={() => setAuthMode("login")}
        onSignup={() => setAuthMode("signup")}
      />

      <main className="relative z-10">
        <Hero
          onLogin={() => setAuthMode("login")}
          onSignup={() => setAuthMode("signup")}
        />
        <Features />
        <Workflow />
        <Spotlights />
        <AIAssistants />
        <LabExperience />
        <Dashboards />
        <Solutions />
        <StatsBand />
        <OtSpotlight />
        <TksaSection />
        <Certification />
      </main>

      <LandingFooter />

      {authMode === "login" && (
        <LoginTerminal
          onClose={closeAuth}
          onSwitchToSignup={() => setAuthMode("signup")}
        />
      )}

      {authMode === "signup" && (
        <SignupTerminal
          onClose={closeAuth}
          onSwitchToLogin={() => setAuthMode("login")}
        />
      )}
    </div>
  );
}
